import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Form, Formik} from 'formik';
import classNames from 'classnames';
import {useIntl} from 'react-intl';
import {useMediaQuery} from 'react-responsive';
import {useNavigate} from 'react-router-dom';
import BaseForm from '../../components/shared/form-controls/base-form';
import {clearEmployees as clearEmployeesList} from '../../features/employees';
import {
    clear as clearEmployeesFilter,
    getAllEmployees,
} from '../../features/employees/short';
import {clearProjects as clearProjectsList} from '../../features/projects';
import {
    clear as clearProjectsFilter,
    getProjects,
} from '../../features/projects/filter';
import {
    downloadTimeTrackingImportTemplate,
    importTimeTracking,
    previewTimeTrackingImport,
} from '../../features/time-tracking-import';
import {
    TimeTrackingImportColumnMapping,
    TimeTrackingImportDataMappingDto,
    TimeTrackingImportPreviewDto,
    TimeTrackingImportSummaryDto,
} from '../../features/time-tracking-import/types';
import {
    clearAbsenceTypes,
    clearProjectTypes,
    clearShiftTypes,
    clearTagTypes,
    getAllAbsencesTypes,
    getAllProjectTypes,
    getAllShiftsTypes,
    getAllTagsTypes,
} from '../../features/types';
import {showSnack} from '../../features/snacks/snackService';
import {useAppDispatch, useAppSelector} from '../../hooks/hooks';
import {Lx} from '../../i18n/consts';
import {NAVIGATION_ROUTES} from '../../routes/NavigationRoute';
import {removeSessionCache, removeSessionCacheByPrefix} from '../../utils/session-cache';
import {
    TimeTrackingImportDataMappingSection,
    TimeTrackingImportMappingCard,
    TimeTrackingImportPreviewSection,
    TimeTrackingImportStepper,
    TimeTrackingImportSummaryBanner,
    TimeTrackingImportUploadCard,
} from './components';
import {DEFAULT_MAPPING, ImportStep, MOBILE_BREAKPOINT} from './constants';
import {
    createInitialMapping,
    extractImportFileHeaders,
    isSupportedFile,
} from './utils/fileHeaders';
import {
    applyEmployeeDataMapping,
    buildMappedImportFile,
    createDefaultDataMappings,
    getEffectiveIssues,
    getRemainingUnresolvedItems,
    isDataMappingComplete,
    normalizeImportValue,
} from './utils/dataMappings';
import {
    buildImportRowTargets,
    EMPTY_FILE_ROW_CHANGES,
    getImportEntityKindColumn,
    getImportOvertimeColumn,
    ImportFileRowChanges,
    ImportRowTarget,
    ManualImportRow,
} from './utils/manualRows';
import styles from './time-tracking-import.module.scss';
import { canImportTimeTracking, canSeeTimesheetPage } from '../../features/profile/entityPermissions';

const getServerErrorMessage = (error: unknown, fallbackMessage: string) => {
    if (typeof error === 'string') {
        return error;
    }

    if (error && typeof error === 'object') {
        const maybeError = error as {
            message?: unknown;
            error?: unknown;
            payload?: unknown;
            response?: {
                data?: unknown;
                statusText?: unknown;
            };
        };
        const responseData = maybeError.response?.data;

        if (typeof responseData === 'string') {
            return responseData;
        }

        if (responseData && typeof responseData === 'object') {
            const data = responseData as {
                message?: unknown;
                error?: unknown;
                title?: unknown;
                detail?: unknown;
                errors?: unknown;
            };
            const dataMessage = data.message ?? data.error ?? data.detail ?? data.title;

            if (typeof dataMessage === 'string') {
                return dataMessage;
            }

            if (data.errors && typeof data.errors === 'object') {
                const firstError = Object.values(data.errors).flat().find(Boolean);

                if (typeof firstError === 'string') {
                    return firstError;
                }
            }
        }

        if (typeof maybeError.payload === 'string') {
            return maybeError.payload;
        }

        const message = maybeError.message ?? maybeError.error ?? maybeError.response?.statusText;

        if (typeof message === 'string') {
            return message;
        }
    }

    return fallbackMessage;
};

const invalidateTimesheetImportCaches = () => {
    removeSessionCache('cache:employees:all');
    removeSessionCache('cache:projects:filter:list');
    removeSessionCache('cache:types:shifts:all');
    removeSessionCacheByPrefix('cache:types:absences:');
};

export default function TimeTrackingImportPage() {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const profile = useAppSelector((store) => store.profile);
    const isMobileLayout = useMediaQuery({maxWidth: MOBILE_BREAKPOINT});
    const pageContentRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [step, setStep] = useState<ImportStep>('upload');
    const [file, setFile] = useState<File | null>(null);
    const [availableColumns, setAvailableColumns] = useState<string[]>([]);
    const [mapping, setMapping] = useState<TimeTrackingImportColumnMapping>(DEFAULT_MAPPING);
    const [dataMappings, setDataMappings] = useState<TimeTrackingImportDataMappingDto[]>([]);
    const [preview, setPreview] = useState<TimeTrackingImportPreviewDto | null>(null);
    const [mappedPreview, setMappedPreview] = useState<TimeTrackingImportPreviewDto | null>(null);
    const [manualRows, setManualRows] = useState<ManualImportRow[]>([]);
    const [fileRowChanges, setFileRowChanges] = useState<ImportFileRowChanges>(EMPTY_FILE_ROW_CHANGES);
    // Snapshot of what the current mappedPreview was built from: the preview uses it to tell which
    // of its rows belongs to which record. Kept apart from the working state so that edits do not
    // drift away from the shown preview while a request is in flight.
    const [previewManualRows, setPreviewManualRows] = useState<ManualImportRow[]>([]);
    const [previewRowTargets, setPreviewRowTargets] = useState<ImportRowTarget[]>([]);
    const [isManualPreviewPending, setIsManualPreviewPending] = useState(false);
    const [summary, setSummary] = useState<TimeTrackingImportSummaryDto | null>(null);
    const [importServerError, setImportServerError] = useState('');
    const [isReadingFile, setIsReadingFile] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isImportLoading, setIsImportLoading] = useState(false);
    const [isTemplateLoading, setIsTemplateLoading] = useState(false);
    const canAccessImport = canImportTimeTracking(profile);
    const canAccessSchedule = canSeeTimesheetPage(profile);

    const skipDuplicates = true;
    const [skipErrors, setSkipErrors] = useState(false);

    useEffect(() => {
        if (canAccessImport) {
            return;
        }

        navigate(canAccessSchedule ? NAVIGATION_ROUTES.TIMESHEET : NAVIGATION_ROUTES.MAIN, { replace: true });
    }, [canAccessImport, canAccessSchedule, navigate]);

    useEffect(() => {
        const scrollContainer = pageContentRef.current?.parentElement;
        if (!scrollContainer) {
            return;
        }

        scrollContainer.scrollTo({top: 0, left: 0, behavior: 'auto'});
    }, [step]);

    const canRunPreview = useMemo(() => {
        return Boolean(
            file
            && (mapping.employeeColumn.trim() || mapping.employeeName.trim())
            && mapping.dateColumn.trim()
            && mapping.hoursColumn.trim()
            && mapping.projectOrAbsenceColumn.trim()
        );
    }, [file, mapping]);

    const hasResolvedDataMappings = useMemo(() => {
        const hasCompleteExplicitMappings = dataMappings.every((dataMapping) => {
            if (dataMapping.action === 'map') {
                return Boolean(dataMapping.targetId);
            }

            return Boolean(dataMapping.targetName?.trim());
        });

        if (!preview || preview.unresolved.length < 1) {
            return hasCompleteExplicitMappings;
        }

        return hasCompleteExplicitMappings && preview.unresolved.every((item) => {
            const currentMapping = dataMappings.find(
                (dataMapping) =>
                    dataMapping.field === item.field
                    && normalizeImportValue(dataMapping.sourceValue) === normalizeImportValue(item.value)
            );

            if (!currentMapping) {
                return false;
            }

            return isDataMappingComplete(currentMapping);
        });
    }, [dataMappings, preview]);

    const importDiagnostics = useMemo(() => {
        const diagnosticsPreview = mappedPreview ?? preview;

        if (!diagnosticsPreview) {
            return {
                hasFile: Boolean(file),
                totalRows: 0,
                remainingErrors: 0,
                remainingUnresolved: 0,
                incompleteMappings: 0,
            };
        }

        const incompleteMappings = dataMappings.filter(
            (dataMapping) => !isDataMappingComplete(dataMapping)
        ).length;
        const remainingErrors = diagnosticsPreview.rows.reduce(
            (count, row) => count + getEffectiveIssues(row, dataMappings, intl).errors.length,
            0
        );
        const remainingUnresolved = getRemainingUnresolvedItems(diagnosticsPreview, dataMappings).length;

        return {
            hasFile: Boolean(file),
            totalRows: diagnosticsPreview.totalRows,
            remainingErrors,
            remainingUnresolved,
            incompleteMappings,
        };
    }, [dataMappings, file, intl, mappedPreview, preview]);

    const canImport = mappedPreview
        ? mappedPreview.totalRows > 0
            && (importDiagnostics.remainingErrors === 0 || skipErrors)
            && importDiagnostics.remainingUnresolved === 0
            && importDiagnostics.incompleteMappings === 0
            && hasResolvedDataMappings
            && !isManualPreviewPending
            && !summary?.success
        : false;

    const importDiagnosticMessage = useMemo(() => {
        if (!file) {
            return intl.formatMessage({id: Lx.TimeTrackingImport.BLOCKED_NO_FILE});
        }

        if (!preview) {
            return intl.formatMessage({id: Lx.TimeTrackingImport.BLOCKED_NO_PREVIEW});
        }

        if (preview.totalRows < 1) {
            return intl.formatMessage({id: Lx.TimeTrackingImport.BLOCKED_NO_ROWS});
        }

        if (canImport || summary?.success) {
            return '';
        }

        return intl.formatMessage(
            {id: Lx.TimeTrackingImport.BLOCKED_DIAGNOSTICS},
            {
                errors: skipErrors ? 0 : importDiagnostics.remainingErrors,
                unresolved: importDiagnostics.remainingUnresolved,
                incomplete: importDiagnostics.incompleteMappings,
            }
        );
    }, [canImport, file, importDiagnostics, intl, preview, skipErrors, summary?.success]);

    const resetImportState = () => {
        setPreview(null);
        setMappedPreview(null);
        setManualRows([]);
        setFileRowChanges(EMPTY_FILE_ROW_CHANGES);
        setPreviewManualRows([]);
        setPreviewRowTargets([]);
        setIsManualPreviewPending(false);
        setSummary(null);
        setDataMappings([]);
        setImportServerError('');
        setSkipErrors(false);
    };

    const handleDataMappingsChange = (nextDataMappings: TimeTrackingImportDataMappingDto[]) => {
        setDataMappings(nextDataMappings);
        setMappedPreview(null);
        setSummary(null);
    };

    const refreshDictionariesAfterImport = () => {
        invalidateTimesheetImportCaches();
        dispatch(clearEmployeesList());
        dispatch(clearEmployeesFilter());
        dispatch(clearProjectsList());
        dispatch(clearProjectsFilter());
        dispatch(clearProjectTypes());
        dispatch(clearShiftTypes());
        dispatch(clearAbsenceTypes());
        dispatch(clearTagTypes());

        void dispatch(getAllEmployees());
        void dispatch(getProjects());
        void dispatch(getAllProjectTypes());
        void dispatch(getAllShiftsTypes());
        void dispatch(getAllAbsencesTypes());
        void dispatch(getAllTagsTypes());
    };

    const handleBack = () => {
        navigate(NAVIGATION_ROUTES.TIMESHEET);
    };

    const openFileDialog = () => {
        inputRef.current?.click();
    };

    const updateMapping = (key: keyof TimeTrackingImportColumnMapping, value: string) => {
        setMapping((current) => ({
            ...current,
            [key]: value,
        }));
        resetImportState();
    };

    const handleDownloadTemplate = async () => {
        setIsTemplateLoading(true);
        try {
            await dispatch(downloadTimeTrackingImportTemplate(undefined)).unwrap();
        } catch (error) {
            console.error('Time tracking import template download failed', error);
            showSnack(
                intl.formatMessage({id: Lx.TimeTrackingImport.DOWNLOAD_TEMPLATE_FAILED}),
                'error'
            );
        } finally {
            setIsTemplateLoading(false);
        }
    };

    const handleFileSelected = async (selectedFile?: File | null) => {
        if (!selectedFile) {
            return;
        }

        if (!isSupportedFile(selectedFile)) {
            showSnack(intl.formatMessage({id: Lx.TimeTrackingImport.UNSUPPORTED_FILE}), 'warning');
            return;
        }

        setIsReadingFile(true);
        try {
            const headers = await extractImportFileHeaders(selectedFile);

            setFile(selectedFile);
            setAvailableColumns(headers);
            setMapping(createInitialMapping(headers));
            resetImportState();
            setStep('mapping');

            if (headers.length < 1) {
                showSnack(
                    intl.formatMessage({id: Lx.TimeTrackingImport.NO_COLUMNS_DETECTED}),
                    'warning'
                );
            }
        } catch (error) {
            console.error('Unable to read import file', error);
            const fallbackMessage = intl.formatMessage({id: Lx.TimeTrackingImport.UNABLE_TO_READ_FILE});
            const errorMessage = getServerErrorMessage(error, fallbackMessage);
            showSnack(errorMessage, 'error');
        } finally {
            setIsReadingFile(false);
        }
    };

    const handlePreview = async () => {
        if (!file || !canRunPreview) {
            showSnack(intl.formatMessage({id: Lx.TimeTrackingImport.FILL_REQUIRED_MAPPINGS}), 'warning');
            return;
        }

        setIsPreviewLoading(true);
        try {
            const result = await dispatch(
                previewTimeTrackingImport({
                    file,
                    ...mapping,
                    skipDuplicates,
                    skipErrors,
                })
            ).unwrap();

            setPreview(result);
            setMappedPreview(null);
            setDataMappings(createDefaultDataMappings(result));
            setSummary(null);
            setStep('dataMapping');
        } catch (error) {
            console.error('Time tracking preview failed', error);
        } finally {
            setIsPreviewLoading(false);
        }
    };

    const requestMappedPreview = async (
        nextManualRows: ManualImportRow[],
        nextFileRowChanges: ImportFileRowChanges
    ) => {
        if (!file || !preview) {
            return;
        }

        const requestMapping = applyEmployeeDataMapping(mapping, dataMappings);
        const mappedFile = buildMappedImportFile(
            file,
            preview,
            dataMappings,
            requestMapping,
            nextManualRows,
            nextFileRowChanges
        );
        const result = await dispatch(
            previewTimeTrackingImport({
                file: mappedFile,
                ...requestMapping,
                skipDuplicates,
                skipErrors,
                dataMappings,
                entityKindColumn: getImportEntityKindColumn(nextManualRows, nextFileRowChanges),
                overtimeColumn: getImportOvertimeColumn(nextManualRows, nextFileRowChanges),
            })
        ).unwrap();

        setMappedPreview(result);
        setPreviewManualRows(nextManualRows);
        setPreviewRowTargets(buildImportRowTargets(
            preview.rows.length,
            nextFileRowChanges,
            nextManualRows
        ));
        setSummary(null);
    };

    const handleDataMappingContinue = async () => {
        if (!file || !preview || !hasResolvedDataMappings) {
            return;
        }

        setIsPreviewLoading(true);
        try {
            await requestMappedPreview(manualRows, fileRowChanges);
            setStep('preview');
        } catch (error) {
            console.error('Time tracking mapped preview failed', error);
        } finally {
            setIsPreviewLoading(false);
        }
    };

    // Row edits are checked by the server: after every change the file is rebuilt and the preview is
    // requested again, so duplicates, permissions and the rest are resolved in the same place as for
    // file rows. On failure the state is rolled back, otherwise the snapshot drifts from the preview.
    const handleImportRowsChange = async (
        nextManualRows: ManualImportRow[],
        nextFileRowChanges: ImportFileRowChanges
    ) => {
        const previousManualRows = manualRows;
        const previousFileRowChanges = fileRowChanges;

        setManualRows(nextManualRows);
        setFileRowChanges(nextFileRowChanges);
        setImportServerError('');
        setIsManualPreviewPending(true);
        try {
            await requestMappedPreview(nextManualRows, nextFileRowChanges);
        } catch (error) {
            console.error('Time tracking manual rows preview failed', error);
            setManualRows(previousManualRows);
            setFileRowChanges(previousFileRowChanges);
            showSnack(
                intl.formatMessage({id: Lx.TimeTrackingImport.MANUAL_ROW_PREVIEW_FAILED}),
                'error'
            );
        } finally {
            setIsManualPreviewPending(false);
        }
    };

    const handleImport = async () => {
        if (!file) {
            showSnack(
                importDiagnosticMessage || intl.formatMessage({id: Lx.TimeTrackingImport.BLOCKED_NO_FILE}),
                'warning'
            );
            return;
        }

        if (!preview || !mappedPreview) {
            showSnack(
                importDiagnosticMessage || intl.formatMessage({id: Lx.TimeTrackingImport.BLOCKED_NO_PREVIEW}),
                'warning'
            );
            return;
        }

        if (!canImport) {
            setImportServerError(importDiagnosticMessage);
            showSnack(importDiagnosticMessage, 'warning');
            return;
        }

        setImportServerError('');
        setIsImportLoading(true);
        try {
            const requestMapping = applyEmployeeDataMapping(mapping, dataMappings);
            const mappedFile = buildMappedImportFile(
                file,
                preview,
                dataMappings,
                requestMapping,
                manualRows,
                fileRowChanges
            );
            const result = await dispatch(
                importTimeTracking({
                    file: mappedFile,
                    ...requestMapping,
                    skipDuplicates,
                    skipErrors,
                    dataMappings,
                    entityKindColumn: getImportEntityKindColumn(manualRows, fileRowChanges),
                    overtimeColumn: getImportOvertimeColumn(manualRows, fileRowChanges),
                })
            ).unwrap();

            setSummary(result);
            refreshDictionariesAfterImport();

            if (result.success) {
                showSnack(
                    intl.formatMessage(
                        {id: Lx.TimeTrackingImport.IMPORTED_ROWS},
                        {count: result.importedShifts + result.importedAbsences}
                    ),
                    'success'
                );
            } else {
                const resultMessage = result.globalErrors?.[0]
                    ?? intl.formatMessage(
                        {id: Lx.TimeTrackingImport.FAILED_WITH_COUNTS},
                        {errors: result.errorRows, unresolved: result.unresolvedRows}
                    );

                setImportServerError(resultMessage);
                showSnack(resultMessage, 'error');
            }
        } catch (error) {
            console.error('Time tracking import failed', error);
            refreshDictionariesAfterImport();
            const errorMessage = getServerErrorMessage(
                error,
                intl.formatMessage({id: Lx.TimeTrackingImport.FAILED_DEFAULT})
            );

            setImportServerError(errorMessage);
            showSnack(errorMessage, 'error');
        } finally {
            setIsImportLoading(false);
        }
    };

    const getPrimaryButtonLabel = (step: string, isSuccess?: boolean) => {
        if (isSuccess) return intl.formatMessage({id: Lx.TimeTrackingImport.RETURN_TO_TIMESHEET});

        switch (step) {
            case 'upload': return intl.formatMessage({id: Lx.TimeTrackingImport.CHOOSE_FILE});
            case 'mapping': return intl.formatMessage({id: Lx.TimeTrackingImport.DATA_MAPPING});
            case 'dataMapping': return intl.formatMessage({id: Lx.TimeTrackingImport.CONTINUE});
            default: return intl.formatMessage({id: Lx.TimeTrackingImport.IMPORT_ROWS});
        }
    };
    const primaryButtonLabel = getPrimaryButtonLabel(step, summary?.success);


    const getSecondaryButtonLabel = (step: string, isSuccess?: boolean) => {
        if (isSuccess) return intl.formatMessage({id: Lx.General.CANCEL});

        switch (step) {
            case 'upload': return intl.formatMessage({id: Lx.General.CANCEL});
            case 'dataMapping': return intl.formatMessage({id: Lx.TimeTrackingImport.EDIT_MAPPING});
            case 'mapping':
            default:
                return intl.formatMessage({id: Lx.TimeTrackingImport.BACK});
        }
    };
    const secondaryButtonLabel = getSecondaryButtonLabel(step, summary?.success);

    const getIsPrimaryActionDisabled = (step: string, isSuccess?: boolean) => {
        if (isSuccess) return false;

        switch (step) {
            case 'upload': 
                return isReadingFile;
            case 'mapping': 
                return !canRunPreview || isPreviewLoading;
            case 'dataMapping':
                return !preview || !hasResolvedDataMappings || isPreviewLoading;
            default:
                return !mappedPreview || isImportLoading || isManualPreviewPending;
        }
    };
    const isPrimaryActionDisabled = getIsPrimaryActionDisabled(step, summary?.success);

    const handlePrimaryAction = async () => {
        if (summary?.success) {
            handleBack();
            return;
        }

        switch (step) {
            case 'upload':
                openFileDialog();
                return;
            case 'mapping':
                await handlePreview();
                return;
            case 'dataMapping':
                await handleDataMappingContinue();
                return;
            default:
                await handleImport();
                return;
        }
    };

    const handleSecondaryAction = () => {
        if (summary?.success) {
            handleBack();
            return;
        }

        switch (step) {
            case 'mapping':
                setStep('upload');
                return;
            case 'preview':
                setStep('dataMapping');
                return;
            case 'dataMapping':
                setStep('mapping');
                return;
            default:
                handleBack();
                return;
        }
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0] ?? null;
        void handleFileSelected(selectedFile);
        event.target.value = '';
    };

    const pageTitle = intl.formatMessage({
        id: Lx.TimeTrackingImport.TITLE,
        defaultMessage: 'Import time tracking',
    });

    const renderStepContent = () => {
        switch (step) {
            case 'upload':
                return (
                    <TimeTrackingImportUploadCard
                        isReadingFile={isReadingFile}
                        isTemplateLoading={isTemplateLoading}
                        onOpenFileDialog={openFileDialog}
                        onDownloadTemplate={() => void handleDownloadTemplate()}
                    />
                );
            case 'mapping':
                return (
                    <TimeTrackingImportMappingCard
                        mapping={mapping}
                        availableColumns={availableColumns}
                        onUpdateMapping={updateMapping}
                    />
                );
            case 'dataMapping':
                return preview ? (
                    <TimeTrackingImportDataMappingSection
                        preview={preview}
                        mappings={dataMappings}
                        onChange={handleDataMappingsChange}
                    />
                ) : null;
            case 'preview':
                if (!mappedPreview) return null;
                return (
                    <div className={styles.previewStepContent}>
                        {importDiagnostics.remainingErrors > 0 && (
                            <section className={classNames(styles.summaryBanner, styles.summaryBannerError)}>
                                <div>
                                    <h3>{intl.formatMessage({id: Lx.TimeTrackingImport.SKIP_ERRORS_TITLE})}</h3>
                                    <p>
                                        {intl.formatMessage(
                                            {id: Lx.TimeTrackingImport.SKIP_ERRORS_DESCRIPTION},
                                            {count: importDiagnostics.remainingErrors}
                                        )}
                                    </p>
                                </div>
                                <label className={styles.skipErrorsOption}>
                                    <input
                                        type='checkbox'
                                        checked={skipErrors}
                                        onChange={(event) => setSkipErrors(event.target.checked)}
                                    />
                                    <span>{intl.formatMessage({id: Lx.TimeTrackingImport.SKIP_ERRORS_LABEL})}</span>
                                </label>
                            </section>
                        )}
                        {importDiagnosticMessage && (
                            <section className={classNames(styles.summaryBanner, styles.summaryBannerError)}>
                                <div>
                                    <h3>{intl.formatMessage({id: Lx.TimeTrackingImport.BLOCKED_TITLE})}</h3>
                                    <p>{importDiagnosticMessage}</p>
                                </div>
                            </section>
                        )}
                        <TimeTrackingImportPreviewSection
                            preview={mappedPreview}
                            dataMappings={dataMappings}
                            manualRows={previewManualRows}
                            rowTargets={previewRowTargets}
                            fileRowChanges={fileRowChanges}
                            onImportRowsChange={(nextManualRows, nextFileRowChanges) =>
                                void handleImportRowsChange(nextManualRows, nextFileRowChanges)}
                            isManualPreviewPending={isManualPreviewPending}
                        />
                    </div>
                );
            default:
                return null;
        }
    };
    if (!canAccessImport) {
        return null;
    }

    return (
        <Formik initialValues={{}} onSubmit={async () => undefined}>
            <Form className={styles.formRoot}>
                <BaseForm
                    contentGap={12}
                    formType='create'
                    formHeader={Lx.TimeTrackingImport.TITLE}
                    formHeaderDefaultMessage='Import time tracking'
                    savingTitle={pageTitle}
                    onClose={handleSecondaryAction}
                    allowPristineSubmit
                    disableDirtyCheck
                    disablePrimaryAction={isPrimaryActionDisabled}
                    onPrimaryAction={handlePrimaryAction}
                    primaryButtonLabel={primaryButtonLabel}
                    secondaryButtonLabel={secondaryButtonLabel}
                    showSuccessSnack={false}
                    bodyClassName={classNames(styles.baseFormBody, {
                        [styles.previewBaseFormBody]: step === 'preview',
                        [styles.dataMappingBaseFormBody]: step === 'dataMapping',
                    })}
                >
                    <input
                        ref={inputRef}
                        type='file'
                        accept='.csv,.xlsx'
                        className={styles.hiddenInput}
                        onChange={handleFileInputChange}
                    />
                    <div
                        ref={pageContentRef}
                        className={classNames(styles.pageScope, styles.pageContent, styles.body, {
                            [styles.mobileLayout]: isMobileLayout,
                            [styles.previewPageContent]: step === 'preview',
                            [styles.dataMappingPageContent]: step === 'dataMapping',
                        })}
                    >
                        <TimeTrackingImportStepper
                            step={step}
                            hasSelectedFile={Boolean(file)}
                            hasPreview={Boolean(preview)}
                        />
                        {summary && <TimeTrackingImportSummaryBanner summary={summary} />}
                        {importServerError && (
                            <section className={classNames(styles.summaryBanner, styles.summaryBannerError)}>
                                <div>
                                    <h3>{intl.formatMessage({id: Lx.TimeTrackingImport.FAILED_TITLE})}</h3>
                                    <p>{importServerError}</p>
                                </div>
                            </section>
                        )}
                        {renderStepContent()}
                    </div>
                </BaseForm>
            </Form>
        </Formik>
    );
}
